import { Socket } from 'socket.io-client';
import { SocketService } from './socket.services';

/**
 * Live driver positions for ONE page (the order map), over the notification
 * socket that is already open. Nothing is polled and nothing is pushed while
 * no page watches: opening the page asks notification-gateway to stream the
 * order's drivers (and switches their app to fast GPS), leaving it stops both.
 *
 * Flow:
 *   page → `track:watch { orderId, role }` → ack `{ drivers }` | `{ error }`
 *   server → `live_mode { on }` whether a driver of the order streams now
 *   server → `live_location { driverId, lat, long, speed?, recordedAt }`
 *            at most one point per driver every ~2 s
 *   page → `track:unwatch` (unmount / tab hidden)
 *
 * Example — inside the order map component:
 *
 *   useEffect(() => {
 *     const stop = LiveTrackingService.watchOrder(
 *       { orderId: Number(orderId), role: 'customer' }, // the role of this page
 *       {
 *         onDrivers: (drivers) => setDrivers(drivers), // [{ driverId, orderTruckId, fleetId? }]
 *         onPoint: (point) => {
 *           // point = { driverId: 'b8e4…', lat: 47.0105, long: 28.8638,
 *           //           speed: 54.2, recordedAt: 1792396800000 }
 *           const truck = drivers.find((d) => d.driverId === point.driverId);
 *           moveMarker(truck?.orderTruckId, [point.lat, point.long]);
 *           setSpeed(formatLiveSpeed(point.speed)); // 54 km/h, "stopped" < 3
 *         },
 *         onError: () => {
 *           // no access / unknown order: keep the 60 s polling of /tracking
 *         },
 *       },
 *     );
 *     return stop; // unwatch on unmount
 *   }, [orderId]);
 *
 * The first position and speed come from GET /{role}/orders/:id/tracking
 * (`telematics.position`, `telematics.speedKmh`, `telematics.recordedAt`);
 * live points only move the marker afterwards. While the socket is
 * connected, the page does not need to poll /tracking at all.
 */

/** Role of the page that opens the map — decides the access check. */
export type LiveViewerRole = 'customer' | 'producer' | 'transporter' | 'admin';

/** One truck of the watched order whose driver streams positions. */
export interface LiveDriver {
	/** Driver uid — matches `LivePoint.driverId`. */
	driverId: string;
	/** Order truck id — `trucks[].id` of the order and /tracking responses. */
	orderTruckId?: number;
	/** Fleet (truck) id, when known. */
	fleetId?: number;
}

/** A live position of one driver. */
export interface LivePoint {
	driverId: string;
	lat: number;
	long: number;
	/** km/h measured by the phone; absent when unknown. */
	speed?: number;
	/** Capture time, epoch ms. */
	recordedAt: number;
}

export interface LiveWatchHandlers {
	onPoint: (point: LivePoint) => void;
	/** The driver app started (`true`) or stopped (`false`) streaming. */
	onLiveMode?: (on: boolean) => void;
	/** The drivers of the order, on every (re)watch. */
	onDrivers?: (drivers: LiveDriver[]) => void;
	/** Access refused or order unknown. */
	onError?: (error: string) => void;
}

export interface LiveWatchParams {
	orderId: number;
	role: LiveViewerRole;
}

export const LIVE_EVENTS = {
	watch: 'track:watch',
	unwatch: 'track:unwatch',
	location: 'live_location',
	liveMode: 'live_mode',
} as const;

/** Below this the truck is shown as stopped (GPS noise while standing). */
export const LIVE_STOPPED_KMH = 3;
/** A point older than this is shown as "last update X min ago", not as speed. */
export const LIVE_STALE_MS = 2 * 60 * 1000;

type WatchAck = { drivers?: LiveDriver[]; error?: string };

export class LiveTrackingService {
	/**
	 * Starts streaming the order's drivers to `handlers.onPoint`. Re-watches
	 * after every reconnect (rooms are per connection) and pauses while the tab
	 * is hidden. Returns the function that stops everything.
	 */
	static watchOrder(
		params: LiveWatchParams,
		handlers: LiveWatchHandlers,
		socket: Socket = SocketService.getInstance(),
	): () => void {
		let stopped = false;

		const watch = () => {
			if (stopped || LiveTrackingService.isHidden()) {
				return;
			}
			socket.emit(LIVE_EVENTS.watch, params, (ack?: WatchAck) => {
				if (stopped) {
					return;
				}
				if (!ack || ack.error) {
					handlers.onError?.(ack?.error || 'No answer');
					return;
				}
				handlers.onDrivers?.(ack.drivers || []);
			});
		};

		const onLocation = (point: LivePoint) => {
			if (!stopped) {
				handlers.onPoint(point);
			}
		};

		const onLiveMode = (mode?: { on?: boolean }) => {
			if (!stopped) {
				handlers.onLiveMode?.(!!mode?.on);
			}
		};

		const onVisibility = () => {
			if (LiveTrackingService.isHidden()) {
				socket.emit(LIVE_EVENTS.unwatch);
			} else {
				watch();
			}
		};

		socket.on(LIVE_EVENTS.location, onLocation);
		socket.on(LIVE_EVENTS.liveMode, onLiveMode);
		socket.on('connect', watch);
		if (typeof document !== 'undefined') {
			document.addEventListener('visibilitychange', onVisibility);
		}
		if (socket.connected) {
			watch();
		}

		return () => {
			if (stopped) {
				return;
			}
			stopped = true;
			socket.emit(LIVE_EVENTS.unwatch);
			socket.off(LIVE_EVENTS.location, onLocation);
			socket.off(LIVE_EVENTS.liveMode, onLiveMode);
			socket.off('connect', watch);
			if (typeof document !== 'undefined') {
				document.removeEventListener('visibilitychange', onVisibility);
			}
		};
	}

	/**
	 * Speed for display: whole km/h, `0` when standing (below
	 * {@link LIVE_STOPPED_KMH}), `undefined` when unknown or when the point is
	 * older than {@link LIVE_STALE_MS} (show its age instead).
	 *
	 *   LiveTrackingService.displaySpeed({ ..., speed: 54.6, recordedAt: now }) // 55
	 *   LiveTrackingService.displaySpeed({ ..., speed: 1.2, recordedAt: now })  // 0 → "stopped"
	 */
	static displaySpeed(
		point: Pick<LivePoint, 'speed' | 'recordedAt'>,
		now: number = Date.now(),
	): number | undefined {
		if (
			typeof point?.speed !== 'number' ||
			!Number.isFinite(point.speed) ||
			now - point.recordedAt > LIVE_STALE_MS
		) {
			return undefined;
		}
		return point.speed < LIVE_STOPPED_KMH ? 0 : Math.round(point.speed);
	}

	private static isHidden(): boolean {
		return (
			typeof document !== 'undefined' && document.visibilityState === 'hidden'
		);
	}
}
